import { Component, OnInit } from '@angular/core';
import { paginationParams } from '@shared/config/table.config';
import { SearchService } from '@services/search.service';
import { ErrorMessages } from '@constants/constants';
import { TimesheetEnum, Priority, TIMESHEET_LABELS, RoutesEnum, PersonaType } from '@constants/global.enum';
import {
  columnDefs,
  exportColumnDefs,
  fontSize_calender,
  TimeSheet,
} from './ops-timesheet-config';
import { Router } from '@angular/router';
import { MonthOption } from '@app/pages/vendor-rep/timesheet/timesheet-config';
import { GlobalErrorService } from '@app/shared/services/global-error.service';
import { VendorService } from '@app/shared/services/vendor.service';
import { TIMESHEET_CONSTANTS } from '@pages/ops-vendor-management/ops-timesheet/ops-timesheet.constants';
import { sortByStatusPriority } from '@shared/helpers/timesheet-sort.helper';
import { getDataLoadedStatus, ComponentType } from '@shared/helpers/timesheet.helper';
import { MatDialog } from '@angular/material/dialog';
import { AddCommentsComponent } from '@app/pages/asset-management/cwr-offboardingCard/Offboarding-DetailsScreen/add-comments/add-comments.component';
import { AlertService } from '@services/alert.service';
import { ExportService } from '@app/shared/services/export.service';
import { ToasterService } from '@services/toaster.service';

@Component({
  selector: 'app-ops-timesheet',
  templateUrl: './ops-timesheet.component.html',
  styleUrl: './ops-timesheet.component.scss',
})
export class OpsTimesheetComponent implements OnInit {
  isCalendarOpen: boolean = false;
  calendarPosition: { top: string; left: string } = {
    top: '0px',
    left: '0px'
  };
  isTimesheetCalendarOpen: boolean = false;
  selectedRowData: any;
  componentName: string = PersonaType.OpsTimesheet;
  columnDefs = columnDefs;
  paginationParams = {
    ...paginationParams,
    pageSizeOptions: [5, 10, 20, 50, 100],
  };
  data: TimeSheet[] = [];
  originalData: TimeSheet[] = [];
  statusFilteredData: TimeSheet[] = [];
  displayData: TimeSheet[] = [];
  filterValue: string = '';
  isStatusFilterApplied: boolean = false;
  filterCriteria: any = {};
  isDataLoaded: boolean = true;
  month: number = 0;
  year: number = 0;
  imageSrc: string = 'assets/img/no-data-found.png';
  errorMsg: string = ErrorMessages.NoDataAvailable;
  priority = Priority;
  opened: boolean = false;
  fontSize_calender = fontSize_calender;
  opsTimesheet = TimesheetEnum;
  timesheetLabels = TIMESHEET_LABELS;
  monthOptions: MonthOption[] = [];
  selectedOption: string = '';
  selectedStatus: any;
  selectedyear: number = new Date().getFullYear();
  selectedmonth: number = new Date().getMonth() + 1;
  statusOptions: MonthOption[] = [
    { label: 'All', value: 'All' },
    ...Object.values(TIMESHEET_CONSTANTS.STATUS).map(val => ({ label: val, value: val }))
  ];
  resetStatus: boolean = true;
  vendorOptions: MonthOption[] = [];
  selectedVendor: any[] = [];
  resetVendor: boolean = true;
  isVendorFilterApplied: boolean = false;
  vendorFilteredData: TimeSheet[] = [];
  dntLeaderOptions: MonthOption[] = [];
  selectedDntLeader: any[] = [];
  resetDntLeader: boolean = true;
  isDntLeaderFilterApplied: boolean = false;
  dntLeaderFilteredData: TimeSheet[] = [];
  employeeStatusOptions: MonthOption[] = [];
  selectedEmployeeStatus: any;
  resetEmployeeStatus: boolean = true;
  isEmployeeStatusFilterApplied: boolean = false;
  employeeStatusFilteredData: TimeSheet[] = [];
  routesEnum = RoutesEnum;
  showContractorProfile = false;
  selectedContractor: TimeSheet | null = null;
  selectedRows: TimeSheet[] = [];
  showExcludeConfirmation: boolean = false;
  pendingExcludeRow: TimeSheet | null = null;
  showDeleteConfirmation: boolean = false;
  pendingDeleteRow: TimeSheet | null = null;
  deleteConfirmationMessage: string = '';
  TIMESHEET_CONSTANTS = TIMESHEET_CONSTANTS;
  // ── NEW: employee details popup ──────────────────────────────
  showEmployeeDetailsPopup: boolean = false;
  selectedEmployeeForDetails: TimeSheet | null = null;

  private readonly incomingNavState: { month?: number; year?: number };

  constructor(
    private searchService: SearchService,
    private router: Router,
    private errorHandler: GlobalErrorService,
    private vendorService: VendorService,
    private exportService: ExportService,
    private toasterService: ToasterService,
  ) {
    this.incomingNavState = this.router.getCurrentNavigation()?.extras?.state ?? {};
  }

  ngOnInit() {
    this.displayData = this.data ?? [];
    this.generateMonthOptions();
    this.applyIncomingMonthYear();
    this.loadVendorOptions();
    this.loadTimesheetData();
  }

  openCalendar(event: MouseEvent, rowData: any): void {
    this.selectedRowData = rowData;
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();

    this.calendarPosition = {
      top: `${rect.bottom + window.scrollY}px`,
      left: `${rect.left + window.scrollX}px`,
    };

    this.isCalendarOpen = true;
    document.body.classList.add('dim-background');
  }

  closeCalendar(): void {
    this.isCalendarOpen = false;
    this.selectedRowData = null;
    document.body.classList.remove('dim-background');
  }

  openTimesheetCalendar(rowData: any): void {
    this.selectedRowData = rowData;
    this.isTimesheetCalendarOpen = true;
  }

  openBillingCalendar(rowData: any): void {
    this.selectedRowData = rowData;
    this.componentName = PersonaType.BillingHours;
    this.isTimesheetCalendarOpen = true;
  }

  closeTimesheetCalendar(): void {
    this.isTimesheetCalendarOpen = false;
    this.selectedRowData = null;
    this.componentName = PersonaType.OpsTimesheet;
  }

  private isDateWithinSelectedMonth(dateStr: string | undefined): boolean {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return false;
    return date.getMonth() + 1 === this.month && date.getFullYear() === this.year;
  }

  clearInput() {
    this.filterValue = '';
    this.applyCurrentFilters();
  }

  searchTimesheet(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(
      event as KeyboardEvent,
    );
    this.applyCurrentFilters();
  }

  generateMonthOptions(): void {
    const today = new Date();
    this.month = today.getMonth() + 1;
    this.year = today.getFullYear();
    this.monthOptions = Array.from({ length: 12 }, (_, i) => {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      return {
        label: `${monthName}, ${year}`,
        value: `${date.getMonth() + 1}-${year}`,
        selected: i === 0,
      };
    });

    if (!this.selectedOption && this.monthOptions.length) {
      this.selectedOption = this.monthOptions[0].value;
    }
  }

  private applyIncomingMonthYear(): void {
    const monthParam = Number(this.incomingNavState.month);
    const yearParam = Number(this.incomingNavState.year);
    if (!monthParam || !yearParam) return;
    const targetValue = `${monthParam}-${yearParam}`;
    if (!this.monthOptions.some((o) => o.value === targetValue)) return;
    this.monthOptions = this.monthOptions.map((o) => ({
      ...o,
      selected: o.value === targetValue,
    }));
    this.selectedOption = targetValue;
    this.month = monthParam;
    this.year = yearParam;
    this.selectedmonth = monthParam;
    this.selectedyear = yearParam;
  }

  onMonthSelect(event: any): void {
    const raw = typeof event === 'string' ? event : event?.value;
    const option = this.monthOptions.find((o) => o.value === raw || o.label === raw);
    if (!option) return;
    this.selectedRows = [];
    this.filterValue = '';
    this.displayData = [];
    this.data = [];
    this.originalData = [];
    this.selectedOption = option.value;
    const [m, y] = option.value.split('-');
    this.month = Number(m);
    this.year = Number(y);
    this.selectedmonth = Number(m);
    this.selectedyear = Number(y);

    this.loadVendorOptions();
    this.loadTimesheetData();
  }

  onStatusSelect(event: any): void {
    this.selectedStatus = event;
    const statusValue = typeof this.selectedStatus === 'string' ? this.selectedStatus : this.selectedStatus?.value;
    
    if (statusValue === 'All') {
      this.isStatusFilterApplied = false;
      this.statusFilteredData = [];
    } else {
      this.isStatusFilterApplied = !!this.selectedStatus;
      
      if (this.selectedStatus) {
        this.statusFilteredData = this.originalData?.filter(item => item?.status === statusValue) || [];
      } else {
        this.statusFilteredData = [];
      }
    }
    
    this.applyCurrentFilters();
  }

  onVendorSelect(event: any): void {

    let processedValues: string[] = [];
    if (Array.isArray(event)) {
      processedValues = event.filter(item => item !== null && item !== undefined);
    } else if (typeof event === 'string' && event.includes(',')) {
      processedValues = event.split(',').map(v => v.trim()).filter(Boolean);
    } else if (event !== null && event !== undefined) {
      processedValues = [event];
    }
    
    this.selectedVendor = processedValues.map(item => 
      typeof item === 'string' ? item : (item as any)?.value
    ).filter(Boolean) as string[];
    this.isVendorFilterApplied = this.selectedVendor && this.selectedVendor.length > 0;
    
    this.applyCurrentFilters();
  }

  onDntLeaderSelect(event: any): void {
    let processedValues: string[] = [];
    
    if (Array.isArray(event)) {
      processedValues = event.filter(item => item !== null && item !== undefined);
    } else if (typeof event === 'string' && event.includes(',')) {
      processedValues = event.split(',').map(v => v.trim()).filter(Boolean);
    } else if (event !== null && event !== undefined) {
      processedValues = [event];
    }

    this.selectedDntLeader = processedValues.map(item => 
      typeof item === 'string' ? item : (item as any)?.value
    ).filter(Boolean) as string[];

    this.isDntLeaderFilterApplied = this.selectedDntLeader && this.selectedDntLeader.length > 0;
    if (this.isDntLeaderFilterApplied) {
      const matchCount = this.originalData?.filter(item => 
        item?.l3ManagerName && this.selectedDntLeader.includes(item.l3ManagerName)
      ).length || 0;
    }
    
    this.applyCurrentFilters();
  }

  onEmployeeStatusSelect(event: any): void {
    this.selectedEmployeeStatus = event;
    const employeeStatusValue = typeof this.selectedEmployeeStatus === 'string' ? this.selectedEmployeeStatus : this.selectedEmployeeStatus?.value;
    
    if (employeeStatusValue === 'All') {
      this.isEmployeeStatusFilterApplied = false;
      this.employeeStatusFilteredData = [];
    } else {
      this.isEmployeeStatusFilterApplied = !!this.selectedEmployeeStatus;
      
      if (this.selectedEmployeeStatus) {
        this.employeeStatusFilteredData = this.originalData?.filter(item => {
          // Handle both "InActive" and "Inactive" variations
          if (employeeStatusValue.toLowerCase() === 'Inactive') {
            return item?.employeeStatus?.toLowerCase() === 'Inactive';
          }
          return item?.employeeStatus === employeeStatusValue;
        }) || [];
      } else {
        this.employeeStatusFilteredData = [];
      }
    }
    
    this.applyCurrentFilters();
  }

   getStatusColor(status: string) {
    const statusMap: Record<string, string> = {
      [this.opsTimesheet.Approved.toLowerCase()]: this.priority.timesheetApproved,
      [this.opsTimesheet.PendingApproval.toLowerCase()]: this.priority.pendingApproval,
      [this.opsTimesheet.Rejected.toLowerCase()]: this.priority.timesheetRejected,
      [this.opsTimesheet.PendingSubmission.toLowerCase()]: this.priority.pendingSubmission,
    };
    return statusMap[status?.toLowerCase() || ''] || '';
  }

  private setMonthAndYear(selectedOption: string) {
    const [monthValue, yearValue] = selectedOption.split(', ');
    const monthStr = new Date(`${monthValue} 1, ${yearValue}`).getMonth() + 1;
    const formattedMonth = monthStr < 10 ? `0${monthStr}` : `${monthStr}`;
    this.month = parseInt(formattedMonth);
    this.year = parseInt(yearValue);
    return this.month + '-' + this.year;
  }

  loadVendorOptions(): void {
    const monthStr = this.month.toString().padStart(2, '0');
    this.vendorService.getOpsVendorList(monthStr, this.year.toString()).subscribe({
      next: (response: any) => {
        const vendors = response?.data || [];
        const uniqueVendorNames = [...new Set(vendors.map((vendor: any) => vendor.vendorName))];
        const vendorOptions = uniqueVendorNames
          .filter((vendorName: any) => vendorName)
          .map((vendorName: any) => ({
            label: vendorName,
            value: vendorName
          }));
        this.vendorOptions = vendorOptions;
      },
      error: (error: any) => {
        this.errorHandler.logComponentError(error);
        this.vendorOptions = [];
      }
    });
  }

  loadDntLeaderOptions(): void {
    const uniqueDntLeaders = [...new Set(
      this.originalData
        .map((item: any) => item.l3ManagerName)
        .filter(Boolean)
    )];
    const dntLeaderOptions = uniqueDntLeaders.map((leader: any) => ({
      label: leader,
      value: leader
    }));
    this.dntLeaderOptions = dntLeaderOptions;
  }

  loadEmployeeStatusOptions(): void {
    const uniqueStatuses = [...new Set(
      this.originalData
        .map((item: any) => item.employeeStatus)
        .filter(Boolean)
    )];
    const normalizedStatuses = new Set<string>();
    uniqueStatuses.forEach((status: string) => {
      if (status.toLowerCase() === 'Inactive') {
        normalizedStatuses.add('Inactive');
      } else if (status.toLowerCase() === 'active') {
        normalizedStatuses.add('Active');
      } else {
        normalizedStatuses.add(status);
      }
    });
    
    const statusOptions = Array.from(normalizedStatuses).map((status: string) => ({
      label: status,
      value: status
    }));
    
    this.employeeStatusOptions = [
      { label: 'All', value: 'All' },
      ...statusOptions
    ];
  }

loadTimesheetData(): void {
  this.vendorService.getOpsTimesheetDetails(this.month, this.year).subscribe(
    (res: any) => {
      let responseData = res?.data || [];
      responseData = responseData.map((item: any) => ({
        ...item,
        showBillingLink: this.isDateWithinSelectedMonth(item.lastHireDate) || this.isDateWithinSelectedMonth(item.lastWorkingDay),
      }));

      responseData = sortByStatusPriority(responseData || [], 'vendor-management');
      this.originalData = responseData;
      this.data = responseData;
      this.loadDntLeaderOptions();
      this.loadEmployeeStatusOptions();

      this.isStatusFilterApplied = false;
      this.statusFilteredData = [];
      this.isVendorFilterApplied = false;
      this.vendorFilteredData = [];
      this.isDntLeaderFilterApplied = false;
      this.dntLeaderFilteredData = [];
      this.isEmployeeStatusFilterApplied = false;
      this.employeeStatusFilteredData = [];

      const statusValue = typeof this.selectedStatus === 'string' ? this.selectedStatus : this.selectedStatus?.value;
      if (this.selectedStatus && statusValue !== 'All') {
        this.isStatusFilterApplied = true;
        this.statusFilteredData = this.originalData?.filter(item => item?.status === statusValue) || [];
      }

      if (this.selectedVendor && this.selectedVendor.length > 0) {
        this.isVendorFilterApplied = true;
      }

      if (this.selectedDntLeader && this.selectedDntLeader.length > 0) {
        this.isDntLeaderFilterApplied = true;
      }

      if (this.selectedEmployeeStatus) {
        const employeeStatusValue = typeof this.selectedEmployeeStatus === 'string' ? this.selectedEmployeeStatus : this.selectedEmployeeStatus?.value;
        if (employeeStatusValue !== 'All') {
          this.isEmployeeStatusFilterApplied = true;
          this.employeeStatusFilteredData = this.originalData?.filter(item => {
            if (employeeStatusValue.toLowerCase() === 'Inactive') {
              return item?.employeeStatus?.toLowerCase() === 'Inactive';
            }
            return item?.employeeStatus === employeeStatusValue;
          }) || [];
        }
      }

      this.applyCurrentFilters();
      this.updateDataLoadedStatus(this.displayData);
    },
    (error: any) => {
      this.errorHandler.logComponentError(error);
      this.errorMsg = ErrorMessages.FailedToLoadTimesheetData;
    }
  );
}

  handleEvent(event: boolean) {
    this.opened = event;
  }

  navigateToVendorDashboard() {
    this.router.navigate(['/ops-vendor-management']);
  }

  excludeFromTimesheets(row: TimeSheet): void {
    this.pendingExcludeRow = row;
    this.showExcludeConfirmation = true;
  }

  onConfirmExclude(): void {
    this.showExcludeConfirmation = false;
    if (!this.pendingExcludeRow) return;
    const row = this.pendingExcludeRow;
    this.pendingExcludeRow = null;
    this.vendorService.excludeEmployeeFromTimesheets(row.employeeId, this.month, this.year).subscribe({
      next: () => {
        this.toasterService.showSuccess('Employee will not be included in timesheets', 'Success');
        this.loadTimesheetData();
      },
      error: () => {
        this.toasterService.showError('Some error occurred', 'Error');
      },
    });
  }

 
  onCancelExclude(): void {
    this.showExcludeConfirmation = false;
    this.pendingExcludeRow = null;
  }

  openDeleteConfirmation(row: TimeSheet): void {
    this.pendingDeleteRow = row;
    this.deleteConfirmationMessage = `Are you sure you want to delete this timesheet record with status <b>${row.status}</b> ?`;
    this.showDeleteConfirmation = true;
  }

  onConfirmDelete(): void {
    this.showDeleteConfirmation = false;
    if (!this.pendingDeleteRow) return;
    const row = this.pendingDeleteRow;
    this.pendingDeleteRow = null;
    this.vendorService.deleteOpsTimesheet(row.employeeId, this.month, this.year).subscribe({
      next: () => {
        this.toasterService.showSuccess('Timesheet deleted successfully', 'Success');
        this.loadTimesheetData();
      },
      error: () => {
        this.toasterService.showError('Failed to delete timesheet. Please try again.', 'Error');
      },
    });
  }

  onCancelDelete(): void {
    this.showDeleteConfirmation = false;
    this.pendingDeleteRow = null;
  }

  // ── NEW: open employee details popup ────────────────────────
  openEmployeeDetails(row: TimeSheet): void {
    this.selectedEmployeeForDetails = row;
    this.showEmployeeDetailsPopup = true;
  }

  // ── NEW: close employee details popup ───────────────────────
  closeEmployeeDetailsPopup(): void {
    this.showEmployeeDetailsPopup = false;
    this.selectedEmployeeForDetails = null;
  }

  // ── NEW: handle last working day update from child ───────────
  onLastWorkingDayUpdated(payload: {
    employeeId: string;
    lastWorkingDay: string | null;
    city: string;
    updatedBy: string;
  }): void {
    if (!payload) return;
    this.vendorService.updateLastWorkingDay(payload).subscribe({
      next: () => {
        this.toasterService.showSuccess('Last working day updated successfully', 'Success');
        this.closeEmployeeDetailsPopup();
        this.loadTimesheetData();
      },
      error: () => {
        this.toasterService.showError('Failed to update last working day. Please try again.', 'Error');
      },
    });
  }

  private applyCurrentFilters(): void {
    this.pageIndex = 1;
    let baseData = [...this.originalData];
    let filterStep = 0;
    
    const statusValue = typeof this.selectedStatus === 'string' ? this.selectedStatus : this.selectedStatus?.value;
    if (this.isStatusFilterApplied && this.selectedStatus && statusValue !== 'All') {
      filterStep++;
      const before = baseData.length;
      baseData = baseData.filter(item => item?.status === statusValue);
    
    }
    
   
    if (this.isVendorFilterApplied && this.selectedVendor && this.selectedVendor.length > 0) {
      filterStep++;
      const before = baseData.length;
      const vendorValues = this.selectedVendor.map(item => 
        typeof item === 'string' ? item : (item as any)?.value || (item as any)?.label
      ).filter(Boolean);
      baseData = baseData.filter(item => item?.vendorName && vendorValues.includes(item.vendorName));
    }

    if (this.isDntLeaderFilterApplied && this.selectedDntLeader && this.selectedDntLeader.length > 0) {
      filterStep++;
      const before = baseData.length;
      const leaderValues = this.selectedDntLeader.map(item => 
        typeof item === 'string' ? item : (item as any)?.value || (item as any)?.label
      ).filter(Boolean);
      baseData = baseData.filter(item => item?.l3ManagerName && leaderValues.includes(item.l3ManagerName));
    }

    const employeeStatusValue = typeof this.selectedEmployeeStatus === 'string' ? this.selectedEmployeeStatus : this.selectedEmployeeStatus?.value;
    if (this.isEmployeeStatusFilterApplied && this.selectedEmployeeStatus && employeeStatusValue !== 'All') {
      filterStep++;
      const before = baseData.length;
      baseData = baseData.filter(item => {
        if (employeeStatusValue.toLowerCase() === 'Inactive') {
          return item?.employeeStatus?.toLowerCase() === 'Inactive';
        }
        return item?.employeeStatus === employeeStatusValue;
      });
    }

    if (this.filterValue && this.filterValue.trim()) {
      filterStep++;
      const before = baseData.length;
      const searchValue = this.filterValue.toLowerCase();
      const numberSearch = !isNaN(Number(searchValue));

      const searchableFields = ['employeeName', 'yearsOfExperience', 'location', 'workingHrs', 'billableHrs', 'leavesAvailed', 'status'];
      
      baseData = baseData.filter(item => {
        if (numberSearch) {
          return searchableFields.some(field => 
            (item as any)[field]?.toString() === searchValue
          );
        } else {
          return searchableFields.some(field =>
            (item as any)[field]?.toString().toLowerCase().includes(searchValue)
          );
        }
      });
    }
    this.displayData = sortByStatusPriority(baseData, 'vendor-management');
    this.updateDataLoadedStatus(this.displayData);
    // A selection never outlives the filter that produced it: rows hidden by the
    // new filter set are dropped, so Download and the header checkbox only ever
    // refer to what is currently in the table.
    if (this.selectedRows.length) {
      const visible = new Set(this.displayData);
      this.selectedRows = this.selectedRows.filter((r) => visible.has(r));
    }
  }

  private updateDataLoadedStatus(data: any[]): void {
    const status = getDataLoadedStatus(data, 'ops-vendor-management' as ComponentType);
    this.isDataLoaded = status.isDataLoaded;
    this.errorMsg = status.errorMsg;
  }
  
  navigateToContractorProfile(data: TimeSheet) {
    this.selectedContractor = data;
    this.showContractorProfile = true;
  }

  onRowSelectionChanged($event: any): void {
    this.selectedRows = $event || [];
  }

  // ── op-table: client-side sort + pagination + row selection ──────────
  sortField: string = 'employeeName';
  sortDir: 'asc' | 'desc' = 'asc';
  pageIndex = 1;
  pageSize = 10;

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
    this.pageIndex = 1;
  }

  get sortedData(): any[] {
    const data = [...(this.displayData || [])];
    const f = this.sortField;
    if (!f) return data;
    data.sort((a: any, b: any) => {
      const av = a?.[f];
      const bv = b?.[f];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return this.sortDir === 'asc' ? cmp : -cmp;
    });
    return data;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.displayData?.length || 0) / this.pageSize));
  }

  get pagedData(): any[] {
    const start = (this.pageIndex - 1) * this.pageSize;
    return this.sortedData.slice(start, start + this.pageSize);
  }

  get rangeStart(): number {
    return (this.displayData?.length || 0) === 0 ? 0 : (this.pageIndex - 1) * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.pageIndex * this.pageSize, this.displayData?.length || 0);
  }

  /** Page numbers to render; -1 is an ellipsis gap. */
  get pageWindow(): number[] {
    const total = this.totalPages;
    const cur = this.pageIndex;
    const out: number[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) out.push(i);
      return out;
    }
    out.push(1);
    const start = Math.max(2, cur - 1);
    const end = Math.min(total - 1, cur + 1);
    if (start > 2) out.push(-1);
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push(-1);
    out.push(total);
    return out;
  }

  goToPage(page: number): void {
    if (page === -1 || page < 1 || page > this.totalPages || page === this.pageIndex) return;
    this.pageIndex = page;
  }

  prevPage(): void {
    this.goToPage(this.pageIndex - 1);
  }

  nextPage(): void {
    this.goToPage(this.pageIndex + 1);
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = +(event.target as HTMLSelectElement).value;
    this.pageIndex = 1;
  }

  trackByIndex(index: number): number {
    return index;
  }

  /* selection — selectedRows feeds download()/export exactly as before */
  isSelected(row: TimeSheet): boolean {
    return this.selectedRows.includes(row);
  }

  toggleRow(row: TimeSheet, checked: boolean): void {
    if (checked) {
      if (!this.selectedRows.includes(row)) this.selectedRows = [...this.selectedRows, row];
    } else {
      this.selectedRows = this.selectedRows.filter((r) => r !== row);
    }
  }

  /** Header checkbox covers every row that passes the current filters, not
   *  just the visible page, so Download picks up all pages. Rows filtered out
   *  are never selected; rows already selected elsewhere are kept. */
  get allSelected(): boolean {
    const all = this.sortedData;
    return all.length > 0 && all.every((r) => this.selectedRows.includes(r));
  }

  /** Partial selection → indeterminate header checkbox. */
  get someSelected(): boolean {
    return this.selectedRows.length > 0 && !this.allSelected;
  }

  toggleAll(checked: boolean): void {
    const all = this.sortedData;
    if (checked) {
      const merged = new Set(this.selectedRows);
      all.forEach((r) => merged.add(r));
      this.selectedRows = [...merged];
    } else {
      this.selectedRows = this.selectedRows.filter((r) => !all.includes(r));
    }
  }

  RetriggerMail(eachTimeSheet: TimeSheet): void {
    const timeSheetWithMonthAndYear = { ...eachTimeSheet, month: this.month, year: this.year };
    this.vendorService.reSendMail(timeSheetWithMonthAndYear).subscribe({
      next: () => {
        this.toasterService.showSuccess('Mail sent successfully', 'Success');
      },
      error: (error) => {
        this.toasterService.showError('Failed to send mail. Please try again.', 'Error');
        this.errorHandler.logComponentError(error);
      },
    });
  }

  download(): void {
    const exportRows = this.prepareExportRows(this.selectedRows);
    this.exportService.handleExport(exportRows, exportColumnDefs, 'ops-timesheet');
  }

  private prepareExportRows(rows: TimeSheet[]): any[] {
    return rows.map(row => {
      const isApproved = row.status === TIMESHEET_CONSTANTS.STATUS.APPROVED;
      const isPendingApproval = row.status === TIMESHEET_CONSTANTS.STATUS.PENDING_APPROVAL;
      return {
        ...row,
        reviewedBy: isApproved ? (row as any).reviewedBy : '',
        reviewedDate: isApproved ? (row as any).reviewedDate : '',
        submittedBy: isPendingApproval ? (row as any).reviewedBy : '',
        submittedDate: isPendingApproval ? (row as any).reviewedDate : '',
      };
    });
  }
}